import React, { useState, useEffect } from "react";
import clsx from "clsx";
import {
  useFormChangeHandler,
  useFormChangeHandlerV2,
} from "hooks/useFormChangeHandler";
import { useSubmitHandler } from "hooks/useSubmitHandler";
import { useCreateData, useUpdateData } from "hooks/useApiHooks";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { IconChevronLeft, IconPlus, IconTrash } from "@tabler/icons-react";
import FormGroup from "./FormGroup";
import { useProduct } from "contexts/v1/masterData/ProductContext";
import { useAgent } from "contexts/v1/masterData/AgentContext";
import { useLabel } from "contexts/v1/masterData/LabelContext";
import { useTrip } from "contexts/v1/masterData/TripContext";
import Swal from "sweetalert2";
import { usePaymentMethod } from "contexts/v1/masterData/PaymentMethodContext";
import { useUser } from "contexts/v1/masterData/UserContext";
import EventSimulator from "components/EventSimulator";
import { sendBookingCreatedEvent, sendBookingUpdatedEvent } from "services/websocket/websocketUtils";

const animatedComponents = makeAnimated();

const CreatePage = ({
  initialData,
  onSuccess,
  onError,
  sessions,
  customers,
  session,
  filterOptions,
  userRoles,
  // labels
}) => {
  // console.log("initialData", initialData);
  const { users } = useUser();
  // console.log("users", users);
  const { products = { data: [] }, refetch } = useProduct();
  const { agents = { data: [] } } = useAgent();
  const agentList = agents.data || [];

  const productList = products.data || [];
  const { labels } = useLabel();
  const labelList = labels?.data || [];
  const { trips, updateFilterTrip } = useTrip(); // 🚀 Gunakan TripContext untuk availability
  const { paymentMethods } = usePaymentMethod(); // 🚀 Gunakan PaymentMethodContext untuk opsi pembayaran

  const API_URL = "/reservation"; // API URL sesuai dengan endpoint booking Anda

  // **Konversi label ke format select option**
  const labelOptions =
    labels?.data?.map((label) => ({
      value: label.id,
      label: label.name,
    })) || [];

  // ambil paymentMethods dari PaymentMethodContext
  const paymentMethodOptions =
    paymentMethods?.data?.map((method) => ({
      value: method.id,
      label: method.name,
    })) || [];

  // **Ambil labels yang sudah dipilih dari initialData**
  const initialSelectedLabels =
    initialData?.label_ids?.map((labelId) => ({
      value: labelId,
      label:
        labelOptions.find((opt) => opt.value === labelId)?.label ||
        `Label ${labelId}`,
    })) || [];

  const [formData, setFormData] = useState({
    // reservation_date: filterOptions?.filters.reservation_date || "", // required|date auto hari ini
    reservation_date:
      filterOptions?.filters.reservation_date ||
      new Date().toISOString().split("T")[0],

    // reservation_date: new Date().toISOString().split("T")[0], // required|date auto hari ini
    // trip_id: "", // required|exists:App\Models\Trip,id gunakan session.id
    trip_id: session?.id || "", // required|exists:App\Models\Trip,id gunakan session.id
    track: "", // nullable|string|max:255,
    agent_id: "", // nullable|exists:App\Models\Agent,id,
    customer_id: "", // nullable|exists:App\Models\Customer,id,
    // label_id: "", // nullable|exists:App\Models\Label,id,
    label_ids: initialSelectedLabels.map((label) => label.value), // 🔥 Simpan hanya ID
    guest_transport: "", // 'required|in:PU,OT,
    guest_pu_time: "", // required_if:guest_transport,PU,
    guest_pu_price: 0, // required_if:guest_transport,PU|numeric|min:0,
    guest_pu_location: "", // required_if:guest_transport,PU|string|max:255,
    guest_pax: null, // required|numeric|min:1,
    // guest_pax: calculateTotalPAX(), // required|numeric|min:1,
    guest_name: "", // required|string|max:255,
    guest_phone: "", // nullable|string|max:20,
    guest_email: "", // nullable|email,
    guest_nationality: "", // nullable|string|max:100,
    guest_mark: "", // nullable|string|max:255,
    save_guest: 1, // nullable|boolean,
    driver: "", // nullable|string|max:255,
    last_number: "", // nullable|string|max:20,
    payment_note: "", // nullable|string|max:100,
    note: "", // nullable|string|max:255,
    activities: [], // required|array
    // label_id: "", // nullable|exists:App\Models\Label,id,
    payment_method_id: "", // nullable|string|max:255,
    tax: 0,
    status: "",
    host_by: "", // nullable|string|max:255,
  });

  const hostByOptions =
    users?.data?.map((user) => ({
      value: user.id,
      label: user.name,
    })) || [];

  // Konversi data agents ke format yang sesuai untuk opsi select
  const agentOption = agentList?.map((agent) => ({
    value: agent.id,
    label: agent.name,
  }));

  // 🔹 Update TripContext saat user mengganti tanggal
  const handleChange = (e, fieldName) => {
    if (fieldName) {
      // setFormData((prev) => ({ ...prev, [fieldName]: e.value }));
      // 🔹 React-Select case: e is an object { value, label }
      setFormData((prev) => ({ ...prev, [fieldName]: e ? e.value : null }));
      return;
    }

    const { id, value } = e.target;

    if (id === "reservation_date") {
      updateFilterTrip("date", value); // 🔹 Update tanggal di TripContext
    }

    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  // 🔹 Mapping data trip dari TripContext ke Select Options
  const sessionOptions =
    trips?.data?.map((trip) => ({
      value: trip.id,
      label: `${trip.name} (${trip.start_time} - ${trip.end_time}) - Sisa ${trip.remaining}`,
      remaining: trip.remaining,
    })) || [];

  const checkAvailabilityV1 = () => {
    const selectedTrip = trips.data.find(
      (trip) => trip.id === formData.trip_id
    );
    if (!selectedTrip) {
      Swal.fire("Error", "Trip tidak ditemukan!", "error");
      return false;
    }

    console.log("🚀 Trip Availability:", selectedTrip);

    // 🔹 Hitung jumlah PAX yang sedang diupdate
    const newGuestPax = formData.guest_pax;
    const previousGuestPax = initialData?.guest_pax || 0; // Default ke 0 jika tidak ada initialData
    const availableSlots = selectedTrip.remaining + previousGuestPax; // Tambahkan pax lama ke slot yang tersedia

    console.log("💡 newGuestPax:", newGuestPax);
    console.log("💡 previousGuestPax:", previousGuestPax);
    console.log("💡 availableSlots:", availableSlots);

    // 🔥 Cek apakah jumlah baru melebihi kapasitas yang tersedia
    if (newGuestPax > availableSlots) {
      Swal.fire(
        "Fully Booked",
        `Trip ${selectedTrip.name} hanya tersisa ${selectedTrip.remaining} slot, termasuk yang sudah dipesan sebelumnya.`,
        "error"
      );
      return false;
    }

    return true;
  };

  useEffect(() => {
    updateFilterTrip("date", formData.reservation_date);
  }, [formData.reservation_date]);

  const checkAvailability = () => {
    const selectedTrip = trips.data.find(
      (trip) => trip.id === formData.trip_id
    );
    if (!selectedTrip) {
      Swal.fire("Error", "Trip tidak ditemukan!", "error");
      return false;
    }

    console.log("🚀 Trip Availability:", selectedTrip);

    // 🔹 Hitung jumlah ATV Single & Tandem yang sedang diupdate
    let totalATV = 0;
    if (formData.activities) {
      formData.activities.forEach((activity) => {
        if (
          activity.activity === "ATV Single" ||
          activity.activity === "ATV Tandem"
        ) {
          totalATV += activity.qty;
        }
      });
    }

    // 🔹 Hitung jumlah ATV sebelum update (jika ada data sebelumnya)
    let previousATV = 0;
    if (initialData?.activities) {
      initialData.activities.forEach((activity) => {
        if (
          activity.activity === "ATV Single" ||
          activity.activity === "ATV Tandem"
        ) {
          previousATV += activity.qty;
        }
      });
    }

    // 🔹 Slot tersedia = jumlah motor tersisa + jumlah motor dari data lama
    const availableSlots = selectedTrip.remaining + previousATV;

    console.log("💡 Total ATV (baru):", totalATV);
    console.log("💡 Total ATV (lama):", previousATV);
    console.log("💡 Available Slots (Motor):", availableSlots);

    // 🔥 Cek apakah jumlah motor yang baru dipesan melebihi kapasitas yang tersedia
    if (totalATV > availableSlots) {
      Swal.fire(
        "Fully Booked",
        `Trip ${selectedTrip.name} hanya tersisa ${selectedTrip.remaining} motor, termasuk yang sudah dipesan sebelumnya.`,
        "error"
      );
      return false;
    }

    return true;
  };

  // Konversi data labels ke format yang sesuai untuk opsi select
  const labelOption = labelList?.map((label) => ({
    value: label.id,
    label: label.name,
  }));

  // trip option isinya A dan B
  const trackOption = [
    { value: "A", label: "A PREMIUM" },
    { value: "B", label: "B REGULAR" },
  ];

  // customers option
  const customerList = customers?.data || [];
  const customerOption = [
    { value: "new", label: "New Customer", isNew: true }, // Tambahkan di awal
    ...customerList?.map((customer) => ({
      value: customer.id,
      label: customer.name,
      // label: `${customer.name} (${customer.nationality})`, // Tampilkan nama dan telepon
    })),
  ];

  const handleCustomerChange = (selectedOption) => {
    if (selectedOption?.value === "new") {
      // Jika New Customer dipilih
      setFormData((prev) => ({
        ...prev,
        customer_id: null, // Kosongkan customer_id
        guest_name: "", // Kosongkan nama
        guest_phone: "",
        guest_email: "",
        guest_pu_location: "",
      }));
    } else {
      // Jika pelanggan dari daftar dipilih
      const selectedCustomer = customerList?.find(
        (customer) => customer.id === selectedOption?.value
      );
      // console.log("Selected Customer:", selectedCustomer); // Debug log
      setFormData((prev) => ({
        ...prev,
        customer_id: selectedCustomer?.id || null, // Simpan id pelanggan
        guest_name: selectedCustomer?.name || "", // Isi nama pelanggan
        guest_phone: selectedCustomer?.phone || "", // Isi telepon pelanggan
        guest_email: selectedCustomer?.email || "", // Isi email pelanggan
        guest_pu_location: selectedCustomer?.address || "", // Isi alamat pelanggan
        save_guest: 0, // Jangan simpan pelanggan baru
      }));
    }
  };

  const isUpdateMode = !!initialData;

  const { createData: createBooking, loading: creatingBooking } = useCreateData(
    API_URL,
    "bookings"
  );
  const { updateData: updateBooking, loading: updatingBooking } = useUpdateData(
    `${API_URL}/${initialData?.id}`,
    "bookings"
  );

  // const handleChange = useFormChangeHandler(setFormData);

  const { handleSubmit, isSubmitting, formError } = useSubmitHandler(
    isUpdateMode ? updateBooking : createBooking,
    async (responseData) => {
      console.log('[CREATE_PAGE] Booking success callback triggered');
      console.log('[CREATE_PAGE] Response data:', responseData);
      console.log('[CREATE_PAGE] Is update mode:', isUpdateMode);

      // Call the original onSuccess callback immediately so the UI doesn't block
      console.log('[CREATE_PAGE] Calling onSuccess callback');
      onSuccess(responseData);

      // Send websocket event for successful booking creation/update asynchronously
      try {
        console.log('[CREATE_PAGE] Attempting to send websocket event...');
        if (isUpdateMode) {
          console.log('[CREATE_PAGE] Sending booking updated event');
          sendBookingUpdatedEvent(responseData).catch(err => console.warn('WS error:', err));
        } else {
          console.log('[CREATE_PAGE] Sending booking created event');
          sendBookingCreatedEvent(responseData).catch(err => console.warn('WS error:', err));
        }
        console.log('[CREATE_PAGE] Websocket event sent successfully');
      } catch (websocketError) {
        console.warn('[CREATE_PAGE] Failed to send booking event:', websocketError);
        // Don't block the success flow if websocket fails
      }
    },
    onError
  );

  const handleBeforeSubmit = (e) => {
    e.preventDefault();

    if (isSubmitting || creatingBooking || updatingBooking) return;

    // 🔍 Cek ketersediaan sebelum booking
    if (!checkAvailability()) return;

    // Validasi khusus untuk PU
    if (formData.guest_transport === "PU" && !formData.guest_pu_time) {
      alert("PU Time wajib diisi jika jenis transportasi adalah Pick Up (PU).");
      return;
    }

    // jika guest_transport adalah OT jangan kirim guest_pu_location
    if (formData.guest_transport === "OT") {
      delete formData.guest_pu_location;
    }

    // Payload data
    const payload = {
      ...formData,
      // label_ids: formData.label_ids, // **Pastikan hanya ID yang dikirim**
      // label_ids: Array.isArray(formData.label_ids) ? formData.label_ids : [],
    };

    handleSubmit(payload, e);
  };

  const guest_transports = [
    { value: "OT", label: "OT" },
    { value: "PU", label: "Pickup & Drop" },
    { value: "P", label: "Pickup Only" },
    { value: "D", label: "Drop Only" },
  ];

  const handleProductSelect = (product) => {
    // Periksa apakah produk sudah ada di activities
    if (formData.activities?.some((item) => item.productId === product.id)) {
      return; // Jika produk sudah dipilih, abaikan
    }

    // Tambahkan produk baru ke dalam activities
    setFormData((prevFormData) => ({
      ...prevFormData,
      activities: [
        ...(prevFormData.activities || []), // Produk sebelumnya
        {
          productId: product.id,
          activity: product.name,
          price: product.price_sale,
          qty: 0, // Default qty
          // type: "product",
          type: product.id === "custom" ? "extra" : "product", // Pastikan type ada
        },
      ],
    }));
  };

  const [selectedCustomProducts, setSelectedCustomProducts] = useState({
    photo: false,
    video: false,
    photo_video: false,
  });

  // Data untuk produk custom
  const customProducts = [
    { id: "photo", name: "1 Pcs Photo", price_sale: 150000 },
    { id: "photo_only", name: "Photo", price_sale: 400000 },
    { id: "video", name: "Video", price_sale: 500000 },
    { id: "photo_video", name: "Photo & Video", price_sale: 750000 },
    { id: "rafting", name: "Rafting", price_sale: 1100000 },
    // transport, private tour
    { id: "transport", name: "Transport", price_sale: 0 },
    { id: "private_tour", name: "Private Tour", price_sale: 0 },
  ];

  // Fungsi untuk menambahkan produk custom
  const handleCustomProductSelect = (product) => {
    // Jika produk sudah dipilih, abaikan klik
    if (selectedCustomProducts[product.id]) return;

    // Tambahkan produk ke formData
    setFormData((prevFormData) => ({
      ...prevFormData,
      activities: [
        ...prevFormData.activities,
        {
          productId: product.id,
          activity: product.name,
          price: product.price_sale,
          qty: 1,
          type: "extra",
        },
      ],
    }));

    // Update status produk custom yang dipilih
    setSelectedCustomProducts((prev) => ({
      ...prev,
      [product.id]: true, // Tandai sebagai sudah dipilih
    }));
  };

  const handleRemoveProduct = (index) => {
    setFormData((prevFormData) => {
      const removedProduct = prevFormData.activities[index];

      // Hapus dari formData.activities
      const updatedActivities = prevFormData.activities.filter(
        (_, i) => i !== index
      );

      return {
        ...prevFormData,
        activities: updatedActivities,
      };
    });

    // Reset status produk custom yang dihapus
    setSelectedCustomProducts((prev) => ({
      ...prev,
      [formData.activities[index].productId]: false,
    }));
  };

  const calculateTotalPAX = (activities = []) => {
    const ATV_SINGLE_ID = "9dfafc54-cdc9-45d1-882b-653b14b0b72b"; // ATV Single dihitung 1 PAX
    const ATV_TANDEM_ID = "9dfafc54-cea9-4d9b-a8ec-26cf7f35ba87"; // ATV Tandem dihitung 2 PAX

    return activities.reduce((sum, item) => {
      const qty = Number(item.qty) || 0; // 🔥 Pastikan konversi ke angka
      if (item.activity === "ATV Single") return sum + qty; // 1 PAX per unit
      if (item.activity === "ATV Tandem") return sum + qty * 2; // 2 PAX per unit
      return sum; // Untuk produk lain
    }, 0);
  };

  // /////

  // **Handle perubahan pada multi-select**
  // const handleLabelChange = (selectedOptions) => {
  //   const selectedIds = selectedOptions ? selectedOptions.map((opt) => opt.value) : [];
  //   setFormData((prev) => ({
  //     ...prev,
  //     label_ids: selectedIds, // ✅ Simpan hanya ID
  //   }));
  // };
  const handleLabelChange = (selectedOptions) => {
    const selectedIds = selectedOptions
      ? selectedOptions.map((opt) => opt.value)
      : [];
    setFormData((prev) => ({
      ...prev,
      label_ids: selectedIds, // 🔥 Simpan hanya ID
    }));
  };

  // **Update state jika initialData berubah**
  useEffect(() => {
    if (initialData?.label_ids) {
      setFormData((prev) => ({
        ...prev,
        label_ids: initialData.label_ids, // 🔥 Ambil hanya ID dari initialData
      }));
    }
  }, [initialData]);

  useEffect(() => {
    if (initialData) {
      setFormData((prev) => ({
        ...prev,
        ...initialData,
        guest_pax: calculateTotalPAX(initialData.activities || []), // 🔥 Pastikan langsung hitung ulang
      }));
    }
  }, [initialData]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      guest_pax: calculateTotalPAX(prev.activities || []), // 🔥 Update otomatis saat activities berubah
    }));
  }, [formData.activities]);

  const handleActivityNameChange = (index, newName) => {
    setFormData((prevFormData) => {
      const updatedActivities = [...prevFormData.activities];
      updatedActivities[index].activity = newName;
      // console.log("Updated Activities:", updatedActivities); // Debug log
      return { ...prevFormData, activities: updatedActivities };
    });
  };

  const handlePriceChange = (index, value) => {
    // Hapus titik (.) dari input harga
    const rawValue = value.replace(/\./g, "");
    const numericValue = parseInt(rawValue, 10) || 0; // Pastikan angka valid

    // Update formData dengan harga baru
    setFormData((prevFormData) => {
      const updatedProducts = [...prevFormData.activities];
      updatedProducts[index].price = numericValue; // Update price dengan nilai angka
      return { ...prevFormData, activities: updatedProducts };
    });
  };

  const handleQuantityChange = (index, newQty) => {
    setFormData((prev) => {
      const updatedActivities = [...prev.activities];
      updatedActivities[index].qty = Number(newQty); // 🔥 Pastikan qty dikonversi ke angka

      return {
        ...prev,
        activities: updatedActivities,
        guest_pax: calculateTotalPAX(updatedActivities), // 🔥 Langsung update guest_pax
      };
    });
  };

  // Fungsi utilitas
  const formatPrice = (price) => {
    return new Intl.NumberFormat("id-ID").format(price);
  };

  const canChange = userRoles?.some((role) =>
    ["superadmin", "admin"].includes(role)
  );

  // **Auto-isi label_ids jika initialData tersedia**
  useEffect(() => {
    if (initialData?.labels) {
      setFormData((prev) => ({
        ...prev,
        label_ids: initialData.labels.map((label) => label.id),
      }));
    }
  }, [initialData]);

  // statusOptions
  const statusOptions = [
    { value: "booked", label: "Booking" },
    { value: "reconfirmed", label: "Re Confirm" },
    { value: "confirmed", label: "Confirmed" },
    { value: "printed", label: "Printed" },
    { value: "played", label: "Playing" },
    { value: "finished", label: "Finish" },
    { value: "cancelled", label: "Cancelled" },
    // <option value="booked">Book</option>
    //     <option value="reconfirmed">Re Confirm</option>
    //     <option value="confirmed">Confirmed</option>
    //     <option value="printed">Printed</option>
    //     <PermissionMiddleware allowedPermissions={["update-play-status"]}>
    //       <option value="played">Playing</option>
    //     </PermissionMiddleware>
    //     <PermissionMiddleware allowedPermissions={["update-finish-status"]}>
    //       <option value="finished">Finish</option>
    //     </PermissionMiddleware>
    //     <option value="cancelled">Cancelled</option>
  ];

  return (
    <form
      onSubmit={handleBeforeSubmit}
      className="d-flex flex-column"
      style={{ height: "100%" }}
    >
      <div className="action-sheet-content flex-grow-1">
        {/*Ketersediaan */}
        <div className="card mb-1">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="card-title">Availability</h5>
            <button
              type="button"
              className="btn btn-outline-info btn-sm"
              onClick={() => {
                const { showCanvasHandler } = require("hooks/useCanvasHandler")();
                showCanvasHandler(
                  "Event Simulator",
                  <EventSimulator />,
                  "fullscreen-canvas"
                );
              }}
              title="Test Realtime Events"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="me-1"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12,6 12,12 16,14"></polyline>
              </svg>
              Event Simulator
            </button>
          </div>
          <div className="card-body">
            <div className="row">
              {/* Tanggal Pemesanan */}
              {/* {canChange && ( */}
              <FormGroup
                id="reservation_date"
                label="Booking Date"
                type="date"
                value={formData.reservation_date}
                onChange={handleChange}
                error={formError?.reservation_date}
                // disabled={isSubmitting}
                disabled={isSubmitting || isUpdateMode}
              />
              {/* )} */}
              {/* Session Select */}
              <FormGroup
                id="trip_id"
                label="Select Trip"
                as="select"
                value={
                  sessionOptions?.find(
                    (option) => option.value === formData.trip_id
                  ) || null
                } // Pastikan ini adalah objek
                onChange={(selectedOption) =>
                  handleChange(selectedOption, "trip_id")
                } // Tangani perubahan nilai
                options={sessionOptions} // Daftar opsi
                placeholder="Select a Trip"
                error={formData.trip_id === "" ? "Please select a trip" : null}
              />
              {/* Trip Option select option A atau B*/}
              <FormGroup
                id="track"
                label="Select Option"
                as="select"
                value={
                  trackOption?.find(
                    (option) => option.value === formData.track
                  ) || null
                } // Pastikan ini adalah objek
                onChange={(selectedOption) =>
                  handleChange(selectedOption, "track")
                } // Tangani perubahan nilai
                options={trackOption} // Daftar opsi
                placeholder="Select a Trip Option"
                error={
                  formData.track === "" ? "Please select a trip option" : null
                }
              />

              {/* tampilkan hasil select trip option jika A tampilkan A jika B tampilkan B */}
              {formData.track === "A" ? (
                <div className="col-md-12 text-center">
                  <div className="alert alert-info" role="alert">
                    <h4 className="alert-heading">Trip Option A</h4>
                    <p>
                      <span style={{ fontSize: "100px", fontWeight: "bold" }}>
                        PREMIUM
                      </span>
                    </p>
                  </div>
                </div>
              ) : formData.track === "B" ? (
                <div className="col-md-12 text-center">
                  <div className="alert alert-google" role="alert">
                    <h4 className="alert-heading">Trip Option B</h4>
                    <p>
                      <span style={{ fontSize: "100px", fontWeight: "bold" }}>
                        REGULAR
                      </span>
                    </p>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* Detail Product */}
        <div className="card mb-1">
          <div className="card-header">
            <h5 className="card-title">Activity Detail</h5>
          </div>
          <div className="card-body">
            <div className="row mb-2">
              <div className="col-12 d-flex justify-content-end gap-3">
                <div className="row mb-2 align-items-center">
                  <label
                    htmlFor="productSelect"
                    className="col-sm-4 col-form-label d-flex justify-content-end"
                    style={{ fontSize: "14px" }}
                  >
                    Activity Select:
                  </label>

                  <div className="col-md-8">
                    <div className="d-flex flex-wrap gap-2">
                      {/* Tombol Produk dari Daftar Product */}
                      {productList.map((product) => (
                        <button
                          key={product.id}
                          type="button"
                          className={`btn ${
                            formData.activities?.some(
                              (item) => item.productId === product.id
                            )
                              ? "btn-primary"
                              : "btn-outline-primary"
                          } btn-pill`}
                          onClick={() => handleProductSelect(product)}
                        >
                          {product.name} (IDR {formatPrice(product.price_sale)})
                        </button>
                      ))}

                      {/* Tombol Custom Produk (Bisa Ditambah Lebih dari Satu) */}
                      {customProducts.map((product) => (
                        <button
                          key={product.id}
                          type="button"
                          className={`btn ${
                            selectedCustomProducts[product.id]
                              ? "btn-warning"
                              : "btn-outline-warning"
                          } btn-pill`}
                          onClick={() => handleCustomProductSelect(product)}
                          disabled={selectedCustomProducts[product.id]} // Nonaktifkan jika sudah dipilih
                        >
                          {product.name} (IDR {formatPrice(product.price_sale)})
                        </button>
                      ))}

                      <button
                        type="button"
                        className="btn btn-outline-dark btn-pill"
                        onClick={() =>
                          handleProductSelect({
                            id: "custom",
                            // name: "Custom Activity",
                            placeholder: "Custom Activity",
                            price_sale: 0,
                            type: "extra",
                          })
                        }
                      >
                        + Custom Activity
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row mt-4">
                {formData?.activities?.map((item, index) => (
                  <div
                    key={index}
                    className="col-12 border rounded p-3 mb-2 bg-light"
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <h6 className="mb-0">{item.activity}</h6>
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => handleRemoveProduct(index)} // Hapus produk
                      >
                        <IconTrash size={18} />
                      </button>
                    </div>
                    <div className="row mt-2">
                      <div className="col-md-4">
                        <label className="form-label">Activity</label>
                        {item.type === "extra" ? (
                          (console.log("Editable Activity:", item), // Debugging log
                          (
                            <input
                              type="text"
                              value={item.activity}
                              className="form-control"
                              onChange={(e) =>
                                handleActivityNameChange(index, e.target.value)
                              }
                              placeholder="Custom Activity"
                              required
                            />
                          ))
                        ) : (
                          <span>{item.activity}</span>
                        )}
                      </div>

                      <div className="col-md-2">
                        <label className="form-label">QTY</label>
                        <input
                          type="text"
                          value={item.qty}
                          min="1"
                          onChange={(e) =>
                            handleQuantityChange(index, e.target.value)
                          }
                          className="form-control"
                        />
                      </div>
                      <div className="col-md-5">
                        <label className="form-label">Price</label>
                        <input
                          type="text"
                          value={formatPrice(item.price)} // Format harga yang sudah disimpan
                          className="form-control"
                          onChange={(e) =>
                            handlePriceChange(index, e.target.value)
                          } // Tangkap input pengguna
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {/* subtotal */}
          <div className="card-footer">
            <div className="d-flex justify-content-end">
              <h3 className="mb-0">
                Sub Total: IDR{" "}
                {formatPrice(
                  formData.activities.reduce(
                    (total, item) => total + item.price * item.qty,
                    0
                  )
                )}
              </h3>
            </div>
          </div>
        </div>

        {/* Informasi Pelanggan */}
        <div className="card mb-1">
          <div className="card-header">
            <h5 className="card-title">Guest Information</h5>
          </div>

          <div className="card-body">
            {/* <FormGroup
              id="customer_id"
              label="Select Customer"
              as="select"
              value={
                customerOption?.find(
                  (option) => option.value === formData.customer_id
                ) || null
              } // Pastikan nilai adalah objek
              onChange={(selectedOption) =>
                handleCustomerChange(selectedOption)
              } // Gunakan handleCustomerChange
              options={customerOption} // Tambahkan opsi "New Customer"
              placeholder="Select a Customer or Create New"
              error={
                formData.customer_id === "" && !formData.guest_name
                  ? "Please select a customer or add a new one"
                  : null
              }
            /> */}
            {/* tampilkan semua form customer */}
            {/* <p>Tampilkan Semua Form Customer</p> */}
            {/* Guest Name */}
            <FormGroup
              id="guest_name"
              label="Guest Name"
              value={formData.guest_name}
              onChange={handleChange}
              error={formError?.guest_name}
              disabled={isSubmitting}
              additionalProps={{ style: { height: "35px" } }}
            />
            {/* Guest guest_nationality */}
            <FormGroup
              id="guest_nationality"
              label="Guest National/Hotel"
              value={formData.guest_nationality}
              onChange={handleChange}
              error={formError?.guest_nationality}
              disabled={isSubmitting}
              additionalProps={{ style: { height: "35px" } }}
            />

            {/* Guest Phone */}
            <FormGroup
              id="guest_phone"
              label="Guest Phone"
              // type="number"
              value={formData.guest_phone}
              onChange={handleChange}
              error={formError?.guest_phone}
              disabled={isSubmitting}
              additionalProps={{ style: { height: "35px" } }}
            />

            {/* Guest Email */}
            <FormGroup
              id="guest_email"
              label="Guest Email"
              type="email"
              value={formData.guest_email}
              onChange={handleChange}
              error={formError?.guest_email}
              disabled={isSubmitting}
              additionalProps={{ style: { height: "35px" } }}
            />
            {/* PU/OT Dropdown */}
            <FormGroup
              id="guest_transport"
              label="PU/OT"
              as="select"
              value={
                guest_transports?.find(
                  (option) => option.value === formData.guest_transport
                ) || null
              }
              onChange={(selectedOption) => {
                const newValue = selectedOption?.value || "";
                setFormData((prev) => ({
                  ...prev,
                  guest_transport: newValue,
                  ...(newValue === "OT" && { guest_pu_time: "" }), // Reset PU Time jika OT dipilih
                }));
              }}
              options={guest_transports}
              error={formError?.guest_transport}
              disabled={isSubmitting}
            />
            {/* PU Time - Ditampilkan hanya jika PU dipilih */}
            {/* {formData.guest_transport === "PU" && ( selain PU juga ada P dan D */}
            {["PU", "P", "D"].includes(formData.guest_transport) && (
              <div className="bg-blue-lt p-3 rounded mt-3">
                <FormGroup
                  id="puTime"
                  label="Time"
                  type="time"
                  value={formData.guest_pu_time}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      guest_pu_time: e.target.value, // Simpan nilai waktu ke formData
                    }))
                  }
                  error={formError?.puTime}
                  disabled={isSubmitting}
                />
                {/* guest_pu_price */}
                {/* <FormGroup
                  id="guest_pu_price"
                  label="Price"
                  type="number"
                  value={formData.guest_pu_price} //formatPrice
                  // value={formatPrice(formData.guest_pu_price)} // Format harga yang sudah disimpan
                  onChange={handleChange}
                  error={formError?.guest_pu_price}
                  disabled={isSubmitting}
                  additionalProps={{ style: { height: "35px" } }}
                /> */}

                {/* guest_pu_location */}
                {/* <FormGroup
                  id="guest_pu_location"
                  label="Location"
                  value={formData.guest_pu_location}
                  onChange={handleChange}
                  error={formError?.guest_pu_location}
                  disabled={isSubmitting}
                  additionalProps={{ style: { height: "35px" } }}
                /> */}
              </div>
            )}

            <FormGroup
              id="guest_pax"
              label="Total PAX"
              type="number"
              // value={formData.guest_pax || 0} // Tampilkan nilai guest_pax
              // value={calculateTotalPAX()} // Hitung total PAX dari activities formData.guest = calculateTotalPAX()
              // value={(formData.guest_pax = calculateTotalPAX())} // Hitung total PAX dari activities formData.guest = calculateTotalPAX()
              value={formData.guest_pax || 0} // Pastikan selalu ada nilai
              onChange={handleChange}
              error={formError?.guest_pax}
              // disabled={true} // Nonaktifkan input manual (karena otomatis dihitung)
              additionalProps={{ style: { height: "35px" } }}
            />

            {/* note guest_mark */}
            <FormGroup
              id="guest_mark"
              label="Guest Mark"
              value={formData.guest_mark}
              onChange={handleChange}
              error={formError?.guest_mark}
              disabled={isSubmitting}
              additionalProps={{ style: { height: "35px" } }}
            />
          </div>
        </div>

        {/* Detail Booking */}
        <div className="card mb-1">
          <div className="card-header">
            <h5 className="card-title">Booking Details</h5>
          </div>
          <div className="card-body">
            
            <FormGroup
              id="agent_id"
              label="Select Agent"
              as="select"
              value={
                agentOption?.find(
                  (option) => option.value === formData.agent_id
                ) || null
              } // Pastikan ini adalah objek
              onChange={(selectedOption) =>
                handleChange(selectedOption, "agent_id")
              } // Tangani perubahan nilai
              options={agentOption} // Daftar opsi
              placeholder="Select a Agent"
              // error={formData.agent_id === "" ? "Please select Agent" : null}
              // disabled jika kondisi update data
              // disabled={isSubmitting || isUpdateMode}
            />

            {/* print json agentOption */}
            {/* <pre>{JSON.stringify(agentOption, null, 2)}</pre> */}

            {/* Customer ID */}

            {/* Multi Select Labels */}
            {/* <div className="form-group mb-3"> */}
            <div className="row mb-1 align-items-center">
              <label
                htmlFor="label_ids"
                className="col-sm-4 col-form-label d-flex justify-content-end"
              >
                Select Labels :
              </label>
              {/* <Select
                id="label_ids"
                name="label_ids"
                value={formData.label_ids} // **Menampilkan label yang sudah dipilih**
                onChange={handleLabelChange} // ✅ Multi-select handler
                options={labelOptions} // **Daftar opsi label**
                isMulti={true} // ✅ Aktifkan multi select
                isClearable
                placeholder="Pilih Label"
                components={animatedComponents}
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 1050 }) }}
              /> */}
              <div className="col-sm-8">
                <Select
                  options={labelOptions}
                  value={labelOptions.filter((opt) =>
                    formData.label_ids.includes(opt.value)
                  )}
                  onChange={handleLabelChange}
                  isMulti
                  isClearable
                  placeholder="Pilih Labels"
                />
              </div>
            </div>

            {/* Driver */}
            <FormGroup
              id="driver"
              label="Driver"
              value={formData.driver}
              onChange={handleChange}
              error={formError?.driver}
              disabled={isSubmitting}
            />

            {/* payment method select */}
            <FormGroup
              id="payment_method_id"
              label="Payment Method"
              as="select"
              value={
                paymentMethodOptions
                  .flatMap((option) =>
                    option.options ? option.options : option
                  )
                  .find((opt) => opt.value === formData.payment_method_id) ||
                null
              }
              onChange={(selectedOption) => {
                // Cari payment method terpilih dari paymentMethods.data
                const selectedId = selectedOption ? selectedOption.value : "";
                const selectedPayment = paymentMethods?.data?.find(
                  (pm) => pm.id === selectedId
                );
                setFormData((prev) => ({
                  ...prev,
                  payment_method_id: selectedId,
                  tax:
                    selectedPayment &&
                    selectedPayment.tax !== undefined &&
                    selectedPayment.tax !== null
                      ? Number(selectedPayment.tax)
                      : 0,
                }));
              }}
              options={paymentMethodOptions}
              isClearable
              placeholder="Select Payment Method"
              error={formError?.payment_method_id}
              disabled={isSubmitting}
            />

            {/* tax */}
            <FormGroup
              id="tax"
              label="Tax %"
              type="number"
              value={formData.tax}
              onChange={handleChange}
              error={formError?.tax}
              disabled={isSubmitting}
            />

            {/* Paymentnote */}
            {/* {canChange && ( */}
            <FormGroup
              id="payment_note"
              label="Payment Note"
              // type="number"
              value={formData.payment_note}
              onChange={handleChange}
              error={formError?.payment_note}
              disabled={isSubmitting}
            />
            {/* )} */}
            {/* 4 Last Number */}
            <FormGroup
              id="last_number"
              label="4 Last Number"
              // type="number"
              value={formData.last_number}
              onChange={handleChange}
              error={formError?.last_number}
              disabled={isSubmitting}
            />
            {/* note message */}
            <FormGroup
              id="note"
              label="Note"
              as="textarea"
              value={formData.note}
              onChange={handleChange}
              error={formError?.note}
              disabled={isSubmitting}
              additionalProps={{ style: { height: "70px" } }}
            />
            {/* select hostby */}
            <div className="row mb-1 align-items-center">
              <label
                htmlFor="label_ids"
                className="col-sm-4 col-form-label d-flex justify-content-end"
              >
                Host By :
              </label>
              <div className="col-sm-8">
                <Select
                  id="host_by"
                  name="host_by"
                  value={
                    hostByOptions.find(
                      (option) => option.value === formData.host_by
                    ) || null
                  }
                  onChange={(selectedOption) =>
                    setFormData((prev) => ({
                      ...prev,
                      host_by: selectedOption ? selectedOption.value : "",
                    }))
                  }
                  options={hostByOptions}
                  isClearable
                  placeholder="Select Host By"
                />
              </div>
            </div>
            {/* tampilkan status */}
            <FormGroup
              id="status"
              label="Status"
              placeholder="Status"
              error={formData.status === "" ? "Please select a status" : null}
              options={statusOptions} // Opsi status
              onChange={(selectedOption) =>
                setFormData((prev) => ({
                  ...prev,
                  status: selectedOption ? selectedOption.value : "", // Simpan hanya value
                }))
              }
              as="select"
              disabled={isSubmitting}
              value={
                statusOptions.find(
                  (option) => option.value === formData.status
                ) || null
              } // Pastikan ini adalah objek
            />
          </div>
        </div>
      </div>

      {/* Submit Button */}
      {/* {!["played", "finished"].includes(formData?.status) && (
        <div className="form-group mt-auto d-grid">
          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={isSubmitting}
          >
            {isUpdateMode ? "Update Booking Host" : "Create Booking"}
          </button>
        </div>
      )} */}

      {(userRoles?.includes("host") &&
        !["played", "finished"].includes(formData?.status)) ||
      !userRoles?.includes("host") ? (
        <div className="form-group mt-auto d-grid">
          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={isSubmitting || creatingBooking || updatingBooking}
          >
            {isUpdateMode ? "Update Booking" : "Create Booking"}
          </button>
        </div>
      ) : null}

      {/* print json formData */}
      {/* <pre>{JSON.stringify(initialData, null, 2)}</pre> */}

      {/* <div className="form-group mt-auto d-grid">
        <button
          type="submit"
          className="btn btn-primary btn-block"
          disabled={isSubmitting}
        >
          {isUpdateMode ? "Update Booking" : "Create Booking"}
        </button>
      </div> */}
    </form>
  );
};

export default CreatePage;

// 📌 Opsi Payment Method untuk Select
export const paymentMethodOptions = [
  { value: "cash", label: "CASH" },
  {
    label: "EDC",
    options: [
      { value: "edc_mandiri", label: "EDC - Mandiri" },
      { value: "edc_bni", label: "EDC - BNI" },
      { value: "edc_bri", label: "EDC - BRI" },
    ],
  },
  {
    label: "TRANSFER",
    options: [
      { value: "transfer_bca", label: "Transfer - BCA" },
      { value: "transfer_mandiri", label: "Transfer - Mandiri" },
    ],
  },
  { value: "voucher", label: "VOUCHER" },
];
